import { useQueryClient } from '@tanstack/react-query'
import {
  useDeleteServicetypesServiceTypeId,
  getGetServicetypesQueryKey,
} from '@/api/generated/service-type/service-type'
import { getViewServiceErrorMessage } from './getViewServiceErrorMessage'
import { getServiceApiErrorBody } from '@/hooks/getServiceApiErrorBody'

/**
 * Deletes a service type. Replaces the `deleteServiceType` thunk + DELETE_SERVICE_TYPE* flow.
 *
 * Old side effects, reproduced via the caller's callbacks:
 * - DELETE_SERVICE_TYPE -> loading: exposed via `isPending`.
 * - DELETE_SERVICE_TYPE_SUCCESS -> the listener called `closeDeleteConfirmationPopUp()` and,
 *   when the deleted id matched the page id, `goToPreviousPage()`. Since this hook is only used
 *   from the page for its own id, both run in `onSuccess`.
 * - DELETE_SERVICE_TYPE_ERROR -> mapped message via `onError(message)`.
 * Invalidates the shared service-types collection so the management list (useServiceTypes) drops
 * the deleted row.
 */
export const useDeleteServiceType = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending } = useDeleteServicetypesServiceTypeId()

  const deleteServiceType = (id: string, opts?: { onSuccess?: () => void; onError?: (message: string) => void }) => {
    mutate(
      { serviceTypeId: id },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetServicetypesQueryKey() })
          opts?.onSuccess?.()
        },
        onError: (error) => {
          opts?.onError?.(getViewServiceErrorMessage(getServiceApiErrorBody(error)))
        },
      },
    )
  }

  return { deleteServiceType, isPending }
}
